import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Panel, Segmented, Textarea, useToast, type Tone } from '../../ui/index.js';

/**
 * Configuration → Platform → Bug reports (066).
 *
 * What people sent with "Report a problem": their words, where they were,
 * what they had just done, and their browser — and where each report has
 * got to. A to-do list, newest first; open ones by default.
 */

type Status = 'new' | 'seen' | 'fixed' | 'wont_fix';

interface Bug {
  id: string;
  at: string;
  tenantId: string | null;
  userName: string | null;
  userEmail: string | null;
  comment: string;
  path: string | null;
  release: string | null;
  userAgent: string | null;
  viewport: string | null;
  theme: string | null;
  recent: { kind: string; name: string; at: string; status?: number }[];
  status: Status;
  handledBy: string | null;
  handledAt: string | null;
  note: string | null;
}

const STATUS: Record<Status, { label: string; tone: Tone }> = {
  new: { label: 'new', tone: 'warn' },
  seen: { label: 'seen', tone: 'info' },
  fixed: { label: 'fixed', tone: 'ok' },
  wont_fix: { label: 'won’t fix', tone: 'neutral' },
};

const when = (iso: string) => new Date(iso).toLocaleString();

function BugRow({ bug }: { bug: Bug }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(bug.note ?? '');
  const move = useMutation({
    mutationFn: (status: Status) => api.patch(`/api/v1/bugs/${bug.id}`, { status, note }),
    onSuccess: (_d, status) => {
      toast.success(`Marked ${STATUS[status].label}`);
      void qc.invalidateQueries({ queryKey: ['bug-reports'] });
    },
    onError: (err) => toast.error('Not saved', err instanceof ApiError ? err.message : 'the change failed'),
  });

  return (
    <li className="rounded-md border border-line p-3">
      <button type="button" className="w-full text-left" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS[bug.status].tone}>{STATUS[bug.status].label}</Badge>
          <span className="text-2xs text-ink-tertiary">
            {when(bug.at)} · {bug.userName ?? bug.userEmail ?? 'someone'}
            {bug.tenantId ? ` · ${bug.tenantId}` : ''}
            {bug.path ? ` · ${bug.path}` : ''}
          </span>
        </div>
        <p className={`mt-1 whitespace-pre-wrap text-sm text-ink ${open ? '' : 'line-clamp-2'}`}>{bug.comment}</p>
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-t border-line-subtle pt-3">
          {bug.recent.length > 0 && (
            <div>
              <div className="text-2xs font-semibold text-ink">What they had just done</div>
              <ol className="mt-1 space-y-0.5 font-mono text-3xs text-ink-secondary">
                {bug.recent.map((e, i) => (
                  <li key={i}>
                    {new Date(e.at).toLocaleTimeString()} · {e.kind} {e.name}
                    {e.status !== undefined ? ` → ${e.status}` : ''}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <dl className="grid grid-cols-[6rem_1fr] gap-x-2 gap-y-0.5 text-2xs">
            <dt className="text-ink-tertiary">Build</dt>
            <dd className="text-ink-secondary">{bug.release ?? '—'}</dd>
            <dt className="text-ink-tertiary">Browser</dt>
            <dd className="break-words text-ink-secondary">{bug.userAgent ?? '—'}</dd>
            <dt className="text-ink-tertiary">Window</dt>
            <dd className="text-ink-secondary">
              {bug.viewport ?? '—'}
              {bug.theme ? ` · ${bug.theme} theme` : ''}
            </dd>
            {bug.handledBy && (
              <>
                <dt className="text-ink-tertiary">Handled</dt>
                <dd className="text-ink-secondary">
                  {bug.handledBy}
                  {bug.handledAt ? `, ${when(bug.handledAt)}` : ''}
                </dd>
              </>
            )}
          </dl>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note — what was done, or why not" aria-label="Note" />
          <div className="flex flex-wrap gap-2">
            {(['seen', 'fixed', 'wont_fix'] as Status[]).map((st) => (
              <Button key={st} variant={st === 'fixed' ? 'primary' : 'secondary'} size="sm" disabled={move.isPending || bug.status === st} onClick={() => move.mutate(st)}>
                Mark {STATUS[st].label}
              </Button>
            ))}
            {bug.status !== 'new' && (
              <Button variant="ghost" size="sm" disabled={move.isPending} onClick={() => move.mutate('new')}>
                Reopen
              </Button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

export function BugReportsPanel() {
  const [which, setWhich] = useState<'open' | 'all'>('open');
  const q = useQuery({
    queryKey: ['bug-reports', which],
    queryFn: () => api.get<{ bugs: Bug[] }>(`/api/v1/bugs?limit=200${which === 'open' ? '&open=true' : ''}`),
  });
  const bugs = q.data?.bugs ?? [];

  return (
    <Panel
      title="Bug reports"
      subtitle="What people sent with “Report a problem”, newest first."
      action={
        <Segmented
          value={which}
          onChange={(v) => setWhich(v)}
          size="sm"
          options={[
            { value: 'open', label: 'Open' },
            { value: 'all', label: 'All' },
          ]}
          aria-label="Which reports"
        />
      }
    >
      {q.isLoading && <p className="text-xs text-ink-tertiary">Loading…</p>}
      {q.error && <p className="text-xs text-danger">Bug reports could not be loaded.</p>}
      {q.data && bugs.length === 0 && (
        <p className="text-xs text-ink-tertiary">{which === 'open' ? 'Nothing open. ' : 'No reports yet.'}</p>
      )}
      <ul className="space-y-2">
        {bugs.map((b) => (
          <BugRow key={b.id} bug={b} />
        ))}
      </ul>
    </Panel>
  );
}
